import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../components/common/Button';
import Toast from '../../components/common/Toast';
import { Competition } from '../../types/interfaces';
import { CompetitionPermissions, errorMessage, saveCompetitionFields } from '../../services/manageApi';
import { sanitizeEvaluationPromptHtml } from '../../utils/evaluationPrompt';
import config from '../../config/environment';
import ReadOnlyField from './ReadOnlyField';

type FieldKind = 'text' | 'textarea' | 'date' | 'number' | 'select';

interface FieldSpec {
  field: string;
  label: string;
  kind: FieldKind;
  options?: string[];
  help?: string;
}

const CONTENT_FIELDS: FieldSpec[] = [
  { field: 'title', label: 'Title', kind: 'text' },
  { field: 'description', label: 'Short description', kind: 'textarea' },
  { field: 'submission', label: 'What to submit', kind: 'textarea', help: 'Shown to participants with the upload form.' },
];

const CORE_FIELDS: FieldSpec[] = [
  { field: 'status', label: 'Status', kind: 'select', options: ['new', 'open', 'closed', 'archived'] },
  { field: 'visibility', label: 'Visibility', kind: 'select', options: ['public', 'private', 'unlisted', 'showcase'] },
  { field: 'prize', label: 'Prize', kind: 'select', options: ['empty', 'money', 'badge'] },
  { field: 'competition_mode', label: 'Mode', kind: 'select', options: ['individual', 'team'] },
  { field: 'start_date', label: 'Start date', kind: 'date' },
  { field: 'end_date', label: 'End date', kind: 'date' },
  { field: 'number_of_submissions', label: 'Submission quota', kind: 'number' },
  { field: 'sponsored_by', label: 'Sponsor', kind: 'text' },
];

const PROMPT_FIELD = 'evaluation';
const ALL_FIELDS = [...CONTENT_FIELDS, ...CORE_FIELDS].map((spec) => spec.field).concat(PROMPT_FIELD);
const SECTION = 'content-details';

type Draft = Record<string, string>;

export function draftFromCompetition(competition: Competition): Draft {
  return {
    title: competition.title ?? '',
    description: competition.description ?? '',
    submission: competition.submission ?? '',
    evaluation: competition.evaluation ?? '',
    status: competition.status ?? '',
    visibility: competition.visibility ?? '',
    prize: competition.prize ?? 'empty',
    competition_mode: competition.competition_mode ?? 'individual',
    start_date: (competition.start_date || '').slice(0, 10),
    end_date: (competition.end_date || '').slice(0, 10),
    number_of_submissions: competition.number_of_submissions == null ? '' : String(competition.number_of_submissions),
    sponsored_by: competition.sponsored_by ?? '',
  };
}

export function changedFields(initial: Draft, draft: Draft, canEdit: (field: string) => boolean): Record<string, unknown> {
  const changes: Record<string, unknown> = {};
  Object.keys(draft).forEach((field) => {
    if (draft[field] === initial[field] || !canEdit(field)) return;
    if (field === 'number_of_submissions') changes[field] = Number(draft[field]);
    else if (field === 'title') changes[field] = draft[field].trim();
    else changes[field] = draft[field];
  });
  return changes;
}

/**
 * Dates and quota are required; they are only checked when the user changed
 * them, so a legacy row that already lacks one can still save other fields.
 */
export function validateDetails(draft: Draft, initial: Draft = draft): string | null {
  const changed = (field: string) => draft[field] !== initial[field];
  const title = draft.title.trim();
  if (!title) return 'Title is required.';
  if (title.length > 255) return 'Title must be at most 255 characters.';
  if ((changed('start_date') || changed('end_date')) && (!draft.start_date || !draft.end_date)) {
    return 'Start and end dates are required.';
  }
  if (draft.start_date && draft.end_date && draft.end_date < draft.start_date) return 'End date must be on or after the start date.';
  if (changed('number_of_submissions')) {
    const raw = draft.number_of_submissions.trim();
    const quota = Number(raw);
    if (!raw || !Number.isInteger(quota) || quota < 1) return 'Submission quota must be a whole number of at least 1.';
  }
  if (draft.sponsored_by.length > 50) return 'Sponsor must be at most 50 characters.';
  return null;
}

interface Props {
  competition: Competition;
  permissions: CompetitionPermissions;
  canEdit: (field: string) => boolean;
  onSaved: (competition: Competition) => void;
  onDirtyChange: (section: string, dirty: boolean) => void;
}

const DetailsForm: React.FC<Props> = ({ competition, permissions, canEdit, onSaved, onDirtyChange }) => {
  const initial = useMemo(() => draftFromCompetition(competition), [competition]);
  const [draft, setDraft] = useState<Draft>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  // When the competition changes (e.g. another section saved), keep the user's
  // unsaved edits and only refresh the fields they have not touched.
  const previousInitial = useRef(initial);
  useEffect(() => {
    const previous = previousInitial.current;
    previousInitial.current = initial;
    if (previous === initial) return;
    setDraft((current) => {
      const next = { ...initial };
      Object.keys(current).forEach((field) => {
        if (current[field] !== previous[field]) next[field] = current[field];
      });
      return next;
    });
  }, [initial]);

  const changes = useMemo(() => changedFields(initial, draft, canEdit), [initial, draft, canEdit]);
  const dirty = Object.keys(changes).length > 0;

  useEffect(() => {
    onDirtyChange(SECTION, dirty);
  }, [dirty, onDirtyChange]);

  const anyEditable = ALL_FIELDS.some(canEdit);
  const update = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { value } = event.target;
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const problem = validateDetails(draft, initial);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await saveCompetitionFields(competition.id, changes, permissions.field_min_role);
      setDraft(draftFromCompetition(updated));
      onSaved(updated);
      setToast('Details saved');
    } catch (err) {
      setError(errorMessage(err, 'Failed to save details'));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (spec: FieldSpec) => {
    if (!canEdit(spec.field)) {
      return (
        <ReadOnlyField
          key={spec.field}
          label={spec.label}
          value={initial[spec.field]}
          requiredRole={permissions.field_min_role[spec.field]}
        />
      );
    }
    const id = `details-${spec.field}`;
    // Team mode can be switched off platform-wide; still keep a competition's saved value selectable.
    const options = spec.field === 'competition_mode' && !config?.FEATURES?.TEAM_MODE_ENABLED
      ? (spec.options || []).filter((option) => option !== 'team' || initial.competition_mode === 'team')
      : spec.options || [];
    const common = {
      id,
      value: draft[spec.field],
      onChange: update(spec.field),
      disabled: saving,
      className: 'input input--default input--md',
    };
    return (
      <div key={spec.field} className="manage-field">
        <label htmlFor={id} className="manage-field__label">{spec.label}</label>
        {spec.kind === 'textarea' ? (
          <textarea {...common} rows={3} />
        ) : spec.kind === 'select' ? (
          <select {...common}>
            {options.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        ) : (
          <input {...common} type={spec.kind === 'number' ? 'number' : spec.kind === 'date' ? 'date' : 'text'} />
        )}
        {spec.help && <span className="manage-field__help">{spec.help}</span>}
      </div>
    );
  };

  return (
    <section className="manage-section" data-tour="manage-details">
      <h3>Details</h3>
      <div className="manage-grid">{CONTENT_FIELDS.map(renderField)}</div>

      {canEdit(PROMPT_FIELD) ? (
        <div className="manage-field">
          <label htmlFor="details-evaluation" className="manage-field__label">Judge prompt (shown above the voting or scoring controls)</label>
          <div className="manage-prompt">
            <textarea
              id="details-evaluation"
              className="input input--default input--md"
              rows={5}
              value={draft.evaluation}
              onChange={update('evaluation')}
              disabled={saving}
            />
            <div
              className="manage-prompt__preview"
              aria-label="Judge prompt preview"
              dangerouslySetInnerHTML={{ __html: sanitizeEvaluationPromptHtml(draft.evaluation) }}
            />
          </div>
          <span className="manage-field__help">
            Allowed formatting: bold, italic, underline, paragraphs, lists and links. Leave empty to use the default prompt.
          </span>
        </div>
      ) : (
        <ReadOnlyField
          label="Judge prompt"
          value={initial.evaluation
            ? <div dangerouslySetInnerHTML={{ __html: sanitizeEvaluationPromptHtml(initial.evaluation) }} />
            : ''}
          requiredRole={permissions.field_min_role.evaluation}
        />
      )}

      <h4 className="manage-subtitle">Competition settings</h4>
      <div className="manage-grid">
        {CORE_FIELDS.map(renderField)}
        <ReadOnlyField label="Type" value={competition.type} note="Set at creation" />
        <ReadOnlyField label="Asset folder (path ID)" value={competition.path_id} note="Set at creation" />
      </div>

      {error && <p className="manage-error" role="alert">{error}</p>}
      {anyEditable && (
        <div className="manage-actions">
          <Button variant="secondary" disabled={!dirty || saving} onClick={() => { setDraft(initial); setError(''); }}>
            Discard
          </Button>
          <Button variant="primary" loading={saving} disabled={!dirty || saving} onClick={handleSave}>
            Save details
          </Button>
        </div>
      )}
      {toast && <Toast message={toast} duration={2500} onClose={() => setToast('')} />}
    </section>
  );
};

export default DetailsForm;
